import { Component, Input, OnInit } from '@angular/core';
import { MatDialog, MatDialogConfig, MatSnackBar, MatRadioChange } from '@angular/material';
import { Business } from '../models/business.model';
import { hasTag, loadTags } from '../../util/tag-utils';
import { Tag } from '../../shared/models/tag.model';
import { BusinessTag } from '../models/business-tag.model';
import { IConditionalSection } from '../../shared/models/condition-section.model';
import { EditTagsComponent } from '../../shared/edit-tags/edit-tags.component';
import { EditToggleService } from '../../shared/services/edit-toggle.service';
import { TagService } from '../../shared/services/tag.service';
import { BusinessManagerService } from '../../shared/user/business-manager.service';

/** Degree tag display names that need an apostrophe to match the University Finder filter labels. */
const DEGREE_DISPLAY_NAME_OVERRIDES: { [tagDisplayName: string]: string } = {
  'Bachelors': 'Bachelor\'s',
  'Masters': 'Master\'s'
};

function formatDegreeTag(tagDisplayName: String): String {
  return DEGREE_DISPLAY_NAME_OVERRIDES[tagDisplayName.toString()] || tagDisplayName;
}

@Component({
  selector: 'app-educational-info',
  templateUrl: './educational-info.component.html',
  styleUrls: ['./educational-info.component.scss']
})
export class EducationalInfoComponent implements OnInit, IConditionalSection {
  @Input() business: Business;
  affiliatesTags: String[] = [];
  degreesTags: String[] = [];
  majorsTags: String[] = [];
  vaApprovedTag: Tag;
  distanceLearningTag: Tag;
  isCollegeApprovedForVaTraining: boolean;
  hasDistanceLearning: boolean;
  hasTag = hasTag;
  editModeSet = false;
  isRegionalManager = false;


  constructor(private _toggleService: EditToggleService,
              private _editEducationalInfoCheckboxDialog: MatDialog,
              private _businessManagerService: BusinessManagerService,
              private _tagService: TagService,
              public snackBar: MatSnackBar) {
      this._toggleService.toggleEmitter.subscribe(isOn => {
        this.editModeSet = isOn;
      });
  }

  async ngOnInit() {
    this.loadAllTags();
    const allTags = await this._tagService.getAllTags();
    this.setApprovedForVA(allTags);
    this.setDistanceLearning(allTags);
    this.isRegionalManager = await this._businessManagerService.isRegionalManager()
      && !await this._businessManagerService.userOwnsBusiness(this.business.id);
  }

  get hasContent(): boolean {
    return this.business &&
    (this.affiliatesTags.length > 0 || this.degreesTags.length > 0 ||
      this.majorsTags.length > 0 || this.hasDistanceLearning || this.isCollegeApprovedForVaTraining);
  }

  openEditEducationalInfoDialog(group: String[], except: String[], title: string): void {
    const dialogConfig = new MatDialogConfig();
    const ERROR_MSG = 'An error has occurred, please try back later, or call 1-800-USA-AOPA if the error persists.';
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;

    dialogConfig.data = {
      business: this.business,
      tagsGroup: group,
      except: except,
      title: title
    };

    dialogConfig.width = '95%';
    const dialogRef = this._editEducationalInfoCheckboxDialog.open(EditTagsComponent, dialogConfig);
    dialogRef.afterClosed().subscribe((isSuccess) => {
      if (isSuccess) {
        this.loadAllTags();
      } else {
        this.snackBar.open(ERROR_MSG, 'Error', {
          duration: 6000
        });
      }
    });
  }

  loadAllTags() {
    this.affiliatesTags = loadTags(this.business.tags, 'College::Accreditation::', '', '');
    this.degreesTags = loadTags(this.business.tags, 'College::Degrees::', '', '').map(formatDegreeTag);
    this.majorsTags = loadTags(this.business.tags, 'College::Majors::', '', '');
  }

  async setApprovedForVA(allTags: BusinessTag[]) {
    this.vaApprovedTag = await this._tagService.findTagByName(allTags, 'College::Approved for VA');
    this.isCollegeApprovedForVaTraining = hasTag(this.business.tags, 'College::Approved for VA');
  }

  async setDistanceLearning(allTags: BusinessTag[]) {
    this.distanceLearningTag = await this._tagService.findTagByName(allTags, 'College::Distance Learning');
    this.hasDistanceLearning = hasTag(this.business.tags, 'College::Distance Learning');
  }

  isCollegeApprovedForVaTrainingChange(radioChange: MatRadioChange) {
    if (radioChange.source.value === '1' && !this.isCollegeApprovedForVaTraining ) {
      this.isCollegeApprovedForVaTraining = true;
      this.updateRadioBtnTagStatus(this.vaApprovedTag, false);
    } else if (radioChange.source.value === '2' && this.isCollegeApprovedForVaTraining) {
      this.isCollegeApprovedForVaTraining = false;
      this.updateRadioBtnTagStatus(this.vaApprovedTag, true);
    }
  }

  hasDistanceLearningChange(radioChange: MatRadioChange) {
    if (radioChange.source.value === '1' && !this.hasDistanceLearning ) {
      this.hasDistanceLearning = true;
      this.updateRadioBtnTagStatus(this.distanceLearningTag, false);
    } else if (radioChange.source.value === '2' && this.hasDistanceLearning) {
      this.hasDistanceLearning = false;
      this.updateRadioBtnTagStatus(this.distanceLearningTag, true);
    }
  }

  async updateRadioBtnTagStatus(tag: Tag, shouldDelete: boolean) {
    const ERROR_MSG = 'An error has occurred, please try back later, or call 1-800-USA-AOPA if the error persists.';
    try {
      const tagId = tag.tagId;
        if (shouldDelete) {
          await this._tagService.deleteTag(tagId, this.business.id);
        } else {
          await this._tagService.postTag(tagId, this.business.id);
        }
        const newBusiness = await this._businessManagerService.updateBusiness(this.business);
        Object.assign(this.business, newBusiness);
    } catch (e) {
      this.snackBar.open(ERROR_MSG, 'Error', {
        duration: 6000
      });
      throw e;
    }
  }

}
